import type { SharedStorage } from "@/env";
import { InstagramAnalyzerTestKeys, TestScore } from "@/pages/instagram.ts";
import { PhotographyType } from "@/features/instagram-analyzer/types.ts";
import { AudienceOrientation } from "@/features/instagram-analyzer/components/InstagramAnalyzer.vue";
import { PaymentResult, PaymentMethodType } from "@/features/billing/services/api";
import type { ProductTypes } from "@/features/cancel-subscription/config/types";
import { IProfileShopProfilesRequest } from "@/features/profile-shop/services/api";
import { ToggleFilterSource } from "@/features/profile-shop/composables/useProfileShopFilters";
import { ProfileLinkSource } from "@/features/profile-shop/utils";

export interface EventMap {
  subscription_webpage_open: SubscriptionPageOpenProps;
  subscription_second_page_open: SubscriptionSecondPageOpenProps;
  subscription_plan_type_selected: SubscriptionPlanTypeSelectedProps;
  subscription_plan_back_clicked: EmptyProps;
  culling_plan_billing_option_selected: CullingPlanBillingOptionSelectedProps;
  subscription_failed: SubscriptionFailedProps;
  coupon_entered: CouponEnteredProps;
  cost_calculator_used: EmptyProps;

  cloud_plans_webpage_open: EmptyProps;
  cloud_plan_update_failed: CloudPlanUpdateFailedProps;
  billing_webpage_open: EmptyProps;
  payment_method_updated: PaymentMethodUpdatedProps;
  account_reactivated: AccountReactivatedProps;
  failed_reactivation: FailedReactivationProps;
  payment_update_failed: PaymentUpdateFailedProps;
  seller_link_clicked: SellerFlowProps;
  seller_flow_next_button_clicked: SellerFlowProps;
  seller_flow_direct_purchase_clicked: SellerFlowProps;
  profile_purchase_failed: ProfilePurchaseFailedProps;
  select_photos_for_test: SelectPhotosForTestProps;
  profile_test: ProfileTestProps;
  cancel_subscription_webpage_open: CancelSubscriptionPageOpenProps;
  cancel_summary_page_open: CancelSummaryPageOpenProps;
  bundle_preview_clear_photos_clicked: BundlePreviewClearPhotosClickedProps;
  bundle_preview_profile_tab_clicked: BundlePreviewProfileTabClickedProps;
  next_preview_button_clicked: NextPreviewButtonClickedProps;
  last_project_unsatisfied_support_clicked: EmptyProps;
  api_playground_testing: EmptyProps;
  paypal_account_added: PaypalAccountAddedProps;
  ig_analyzer_open: EmptyProps;
  ig_imagen_cta_clicked: IgImagenCtaClickedProps;
  ig_analyze_cta_clicked: IgAnalyzeCtaClicked;
  ig_get_insights_clicked: IgGetInsightsClickedProps;
  ig_share_clicked: IgShareClickedProps;
  ig_share_tip_clicked: IgShareTipClickedProps;
  ig_expand_section_clicked: IgExpandSectionClickedProps;
  ig_read_more_clicked: IgReadMoreClickedProps;
  ig_next_tip_clicked: IgNextTipClickedProps;
  ig_error_modal_opened: EmptyProps;

  credit_packages_purchase_completed: CreditPackagePurchase;
  credit_packages_payment_failed: CreditPackagePaymentFailedProps;
  credit_packages_web_page_open: CPWebPageOpen;
  credit_packages_interacted_with_calculator: EmptyProps;
  credit_packages_second_page_open: CPSecondPageOpenProps;
  credit_packages_annual_plan_modal_open: CPSecondPageOpenProps;
  annual_plan_payment_failed: AnnualPlanPaymentFailedProps;
  vat_id_added: EmptyProps;

  pictime_integration_open: EmptyProps;

  clarity_consent_accepted: EmptyProps;
  clarity_consent_rejected: EmptyProps;
  profile_shop_webpage_open: ProfileShopWebpageOpenProps;
  profile_shop_confirmation_clicked: ProfileShopConfirmationClickedProps;
  profile_shop_filters_selected: ProfileShopFiltersSelectedProps;
  profile_shop_low_results: ProfileShopFiltersSelectedProps;
  profile_shop_filters_reset: ProfileShopFilterResetProps;
  profile_shop_compare_style_photo_selected: ProfileShopCompareStylePhotoSelectedProps;
  profile_shop_banner_clicked: ProfileShopBannerClickedProps;

  purchase_annual_plan_web_page_open: BaseAnnualPlanPurchaseProps;
  purchase_annual_plan_second_page_open: AnnualPlanPurchaseClickedProps;
}

export type Events = keyof EventMap;
type EventGroup =
  | "general"
  | "subscription_account_web"
  | "backup_plans_account_web"
  | "update_payment_method_web"
  | "sell_a_profile"
  | "cancel_subscription_account_web"
  | "api_playground"
  | "instagram_analyzer"
  | "credit_packages"
  | "edit_to_delivery"
  | "profile_shop"
  | "annual_plans";

export const eventMetadata: Record<Events, { eventGroup: EventGroup }> = {
  subscription_webpage_open: { eventGroup: "subscription_account_web" },
  subscription_second_page_open: { eventGroup: "subscription_account_web" },
  subscription_plan_type_selected: { eventGroup: "subscription_account_web" },
  subscription_plan_back_clicked: { eventGroup: "subscription_account_web" },
  culling_plan_billing_option_selected: { eventGroup: "subscription_account_web" },
  subscription_failed: { eventGroup: "subscription_account_web" },
  cost_calculator_used: { eventGroup: "subscription_account_web" },
  coupon_entered: { eventGroup: "subscription_account_web" },

  cloud_plans_webpage_open: { eventGroup: "backup_plans_account_web" },
  cloud_plan_update_failed: { eventGroup: "backup_plans_account_web" },
  payment_method_updated: { eventGroup: "update_payment_method_web" },
  payment_update_failed: { eventGroup: "update_payment_method_web" },
  billing_webpage_open: { eventGroup: "update_payment_method_web" },
  account_reactivated: { eventGroup: "update_payment_method_web" },
  paypal_account_added: { eventGroup: "update_payment_method_web" },
  failed_reactivation: { eventGroup: "update_payment_method_web" },
  seller_link_clicked: { eventGroup: "sell_a_profile" },
  seller_flow_next_button_clicked: { eventGroup: "sell_a_profile" },
  profile_purchase_failed: { eventGroup: "sell_a_profile" },
  bundle_preview_clear_photos_clicked: { eventGroup: "sell_a_profile" },
  bundle_preview_profile_tab_clicked: { eventGroup: "sell_a_profile" },
  select_photos_for_test: { eventGroup: "sell_a_profile" },
  profile_test: { eventGroup: "sell_a_profile" },
  next_preview_button_clicked: { eventGroup: "sell_a_profile" },
  seller_flow_direct_purchase_clicked: { eventGroup: "sell_a_profile" },
  cancel_subscription_webpage_open: { eventGroup: "cancel_subscription_account_web" },
  last_project_unsatisfied_support_clicked: { eventGroup: "cancel_subscription_account_web" },
  cancel_summary_page_open: { eventGroup: "cancel_subscription_account_web" },
  api_playground_testing: { eventGroup: "api_playground" },
  ig_analyze_cta_clicked: { eventGroup: "instagram_analyzer" },
  ig_analyzer_open: { eventGroup: "instagram_analyzer" },
  ig_error_modal_opened: { eventGroup: "instagram_analyzer" },
  ig_expand_section_clicked: { eventGroup: "instagram_analyzer" },
  ig_get_insights_clicked: { eventGroup: "instagram_analyzer" },
  ig_imagen_cta_clicked: { eventGroup: "instagram_analyzer" },
  ig_next_tip_clicked: { eventGroup: "instagram_analyzer" },
  ig_read_more_clicked: { eventGroup: "instagram_analyzer" },
  ig_share_clicked: { eventGroup: "instagram_analyzer" },
  ig_share_tip_clicked: { eventGroup: "instagram_analyzer" },
  credit_packages_payment_failed: { eventGroup: "credit_packages" },
  credit_packages_purchase_completed: { eventGroup: "credit_packages" },
  credit_packages_web_page_open: { eventGroup: "credit_packages" },
  credit_packages_interacted_with_calculator: { eventGroup: "credit_packages" },
  credit_packages_second_page_open: { eventGroup: "credit_packages" },
  credit_packages_annual_plan_modal_open: { eventGroup: "credit_packages" },
  annual_plan_payment_failed: { eventGroup: "credit_packages" },
  vat_id_added: { eventGroup: "subscription_account_web" },
  pictime_integration_open: { eventGroup: "edit_to_delivery" },
  clarity_consent_accepted: { eventGroup: "general" },
  clarity_consent_rejected: { eventGroup: "general" },
  profile_shop_webpage_open: { eventGroup: "profile_shop" },
  profile_shop_confirmation_clicked: { eventGroup: "profile_shop" },
  profile_shop_filters_selected: { eventGroup: "profile_shop" },
  profile_shop_low_results: { eventGroup: "profile_shop" },
  profile_shop_filters_reset: { eventGroup: "profile_shop" },
  profile_shop_banner_clicked: { eventGroup: "profile_shop" },
  profile_shop_compare_style_photo_selected: { eventGroup: "profile_shop" },

  purchase_annual_plan_web_page_open: { eventGroup: "annual_plans" },
  purchase_annual_plan_second_page_open: { eventGroup: "annual_plans" },
};

interface BaseAnnualPlanPurchaseProps {
  campaign_name?: string | null;
  is_event_offer?: boolean;
  is_mobile?: boolean;
  currency?: string;
}
interface AnnualPlanPurchaseClickedProps extends BaseAnnualPlanPurchaseProps {
  plan_id: string;
}

interface CreditPackagePaymentFailedProps {
  payment_method?: PaymentMethodType;
  stripe_error_message: string;
  package_id: string;
}
interface CPSecondPageOpenProps {
  package_id: number;
  is_mobile?: boolean;
}
interface CPWebPageOpen {
  device: "desktop" | "mobile";
}
interface CreditPackagePurchase {
  package_id: string;
  payment_method?: PaymentMethodType;
  payment_response?: PaymentResult["payment_response"];
}

type EmptyProps = Record<string, never>;

interface IgImagenCtaClickedProps {
  user_score?: number;
  cta_copy: "Header" | "Footer" | "Try Imagen now";
}

interface IgShareTipClickedProps {
  user_score: number;
  section_name: InstagramAnalyzerTestKeys;
  tip_number: number;
}

interface IgExpandSectionClickedProps {
  user_score: number;
  section_name: InstagramAnalyzerTestKeys;
  test_score?: TestScore;
  state: "collapsed" | "expanded";
}

interface IgReadMoreClickedProps {
  user_score: number;
  section_name: InstagramAnalyzerTestKeys;
  test_score?: TestScore;
  state: "collapsed" | "expanded";
}

interface IgNextTipClickedProps {
  current_tip_number: number;
  section_name: InstagramAnalyzerTestKeys;
}

interface IgGetInsightsClickedProps {
  user_handle: string;
  user_audience: AudienceOrientation;
  user_genres: PhotographyType[];
}

interface IgAnalyzeCtaClicked {
  user_handle: string;
}

interface IgShareClickedProps {
  placement: "top" | "bottom";
  user_score: number;
}

export type CouponEnteredScreenName = "SUBSCRIBE" | "SELL_A_PROFILE";

interface CouponEnteredProps {
  coupon_name: string;
  activation_success: boolean;
  cause_of_failure?: string;
  screen_name: CouponEnteredScreenName;
}

export type CheckoutPageType = "culling_checkout" | "trial_checkout" | "old_checkout";
interface SubscriptionPageOpenProps {
  checkout_page_type: CheckoutPageType;
  event_offer: boolean;
}

interface SubscriptionSecondPageOpenProps {
  culling_plan_type?: "monthly" | "annually";
}

interface SubscriptionPlanTypeSelectedProps {
  selected_plan: "edit_only" | "edit_and_culling";
}

interface SubscriptionFailedProps {
  error_message: string;
}

interface ProfilePurchaseFailedProps {
  payment_method?: PaymentMethodType;
  error_message: string;
  user_flow: ProfileUserFlow;
}
interface CloudPlanUpdateFailedProps {
  cause_of_failure: string;
}
interface FunnelPaymentMethodDetails {
  payment_method: PaymentMethodType;
  brand?: string;
  card_last_digits?: string;
  expiration_month?: number;
  expiration_year?: number;
  paypal_email?: string;
}
interface PaymentMethodUpdatedProps {
  billing_details: FunnelPaymentMethodDetails;
}

type SuspensionStatus = "After suspension" | "Before suspension";

interface AccountReactivatedProps {
  billing_details: FunnelPaymentMethodDetails;
  suspension_status: SuspensionStatus;
}

interface FailedConfirmSetupProps {
  payment_method: PaymentMethodType;
  vendor_code?: string;
  issuer_code?: string;
  message?: string;
  param?: string;
  suspension_status: SuspensionStatus;
}

interface CullingPlanBillingOptionSelectedProps {
  culling_plan_type: "monthly" | "annually";
}

interface SellerFlowProps {
  seller_name: string;
  bundle_name: string;
  bundle_id: string;
  seller_source: string;
  user_flow: ProfileUserFlow;
  source?: ProfileLinkSource;
  profile_tags?: string[];
}

type FailedReactivationProps = FailedConfirmSetupProps | { message?: string };
type PaymentUpdateFailedProps = FailedConfirmSetupProps | { message?: string };

export const MARKETING_URL_PARAMS: Array<keyof SharedStorage> = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "ref_id",
  "ref",
  "campaignid",
  "keyword",
  "AdSetName",
  "adgroupid",
  "matchtype",
  "network",
  "gclid",
  "ttclid",
  "leadid",
];

export type SelectPhotosMethod = "BROWSE" | "DRAG_AND_DROP";
interface SelectPhotosForTestProps {
  method: SelectPhotosMethod;
  number_of_photos: number;
  seller_name: string;
  bundle_name: string;
  seller_source: string;
  profile_tags?: string[];
  user_flow: ProfileUserFlow;
}

interface ProfileTestProps {
  seller_name: string;
  bundle_name: string;
  seller_source: string;
  profile_tags?: string[];
  user_flow: ProfileUserFlow;
}

interface CancelSubscriptionPageOpenProps {
  page_version: "v2 - web account";
  is_last_project_unsatisfied: boolean;
  product_types: ProductTypes;
}

interface CancelSummaryPageOpenProps {
  main_reason: string;
  secondary_reasons: string;
  tertiary_reasons: string;
}

interface BundlePreviewClearPhotosClickedProps {
  bundle_name: string;
  seller_name: string;
  seller_source: string;
  user_flow: ProfileUserFlow;
}

interface BundlePreviewProfileTabClickedProps {
  bundle_name: string;
  seller_name: string;
  seller_source: string;
  profile_name: string;
  user_flow: ProfileUserFlow;
}

interface NextPreviewButtonClickedProps {
  is_used_preview: boolean;
  user_flow: ProfileUserFlow;
}

interface PaypalAccountAddedProps {
  source: string;
}

interface ProfileShopWebpageOpenProps {
  entry_point: string;
}

interface ProfileShopCompareStylePhotoSelectedProps {
  photo_selected: string;
}

type ProfileUserFlow = "purchase_profile" | "profile-shop";

interface ProfileShopConfirmationClickedProps {
  button_name: "OPEN_IMAGEN" | "CONTINUE_SHOPPING";
  user_flow: ProfileUserFlow;
}

interface ProfileShopFiltersSelectedProps extends IProfileShopProfilesRequest {
  page: number;
  page_size: number;
  type: string[];
  style: string[];
  format: string[];
  genres: string[];
  priceMin: number;
  priceMax: number;
  priceCurrency: string;
  sort: string;
  number_of_results?: number;
  selection_source?: ToggleFilterSource;
}

interface ProfileShopFilterResetProps {
  reset_source: "empty_state" | "filter_panel";
}

interface ProfileShopBannerClickedProps {
  campaign_name: string;
  link: string;
}

interface AnnualPlanPaymentFailedProps {
  plan_id?: string;
  error_type: string;
  error_message: string;
}
